import React, { useMemo, useState, useRef, useEffect } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { restrictToParentElement } from '@dnd-kit/modifiers'
import { StructureContainer, StructureHeader, Title } from '../style'
import InputFiles from '../../../../../components/ui/InputFiles'
import AttachmentSourceModal from '../../../../../components/AttachmentSourceModal'
import AttachmentGalleryModal, { GalleryFile } from '../../../../../components/AttachmentGalleryModal'
import AttachmentApiService from '../../../../../services/AttachmentApiService'
import {
  EditInfosAbout,
  Image,
  ImageContainer,
  ImageInfo,
  ImageOrderBadge,
  ImagesContainer,
  RemoveImage,
  TitleOrSubtitle,
  TitleOrSubtitleInput,
  DragHandle,
} from './style'
import { GripVertical, ImageIcon, Pen, X, Loader2 } from 'lucide-react'
import { getSignedAttachmentUrl } from '../../../../../utils/openSignedAttachment'

interface ImageData {
  id?: number
  file?: File
  fileUrl?: string
  fileSize?: number
  customName: string
  isEditing: boolean
  subtitle?: string
}

interface ImagesInTheCertificateProps {
  images: ImageData[]
  setImages: React.Dispatch<React.SetStateAction<ImageData[]>>
}

const getSortableId = (imageData: ImageData, index: number) => {
  if (imageData.id != null) return `id-${imageData.id}`
  if (imageData.fileUrl) return `url-${imageData.fileUrl}`
  if (imageData.file) {
    return `file-${imageData.file.name}-${imageData.file.size}-${imageData.file.lastModified}`
  }
  return `idx-${index}-${imageData.customName}`
}

const getImageSize = (imageData: ImageData) => {
  if (imageData.file) return `${(imageData.file.size / 1024 / 1024).toFixed(1)} MB`
  if (imageData.fileSize && imageData.fileSize > 0) {
    return `${(imageData.fileSize / 1024 / 1024).toFixed(1)} MB`
  }
  return '-'
}

type SortableImageProps = {
  imageData: ImageData
  index: number
  sortableId: string
  onRemove: (index: number) => void
  onStartEditing: (index: number) => void
  onSaveEdit: (index: number, newName: string) => void
  onCancelEdit: (index: number) => void
  onChangeCustomName: (index: number, value: string) => void
  onChangeSubtitle: (index: number, value: string) => void
}

const SortableImageCard: React.FC<SortableImageProps> = ({
  imageData,
  index,
  sortableId,
  onRemove,
  onStartEditing,
  onSaveEdit,
  onCancelEdit,
  onChangeCustomName,
  onChangeSubtitle,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1,
    zIndex: isDragging ? 2 : undefined,
  }

  const [displayUrl, setDisplayUrl] = useState<string>('')
  const [imageError, setImageError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setImageError(false)
    if (imageData.file) {
      const objUrl = URL.createObjectURL(imageData.file)
      setDisplayUrl(objUrl)
      return () => {
        URL.revokeObjectURL(objUrl)
      }
    }

    if (imageData.fileUrl) {
      const fileUrl = imageData.fileUrl
      if (
        fileUrl.startsWith('blob:') ||
        fileUrl.startsWith('data:') ||
        fileUrl.includes('X-Goog-Signature')
      ) {
        setDisplayUrl(fileUrl)
        setImageError(false)
        return
      }

      let isMounted = true
      setIsLoading(true)
      getSignedAttachmentUrl(fileUrl)
        .then((url) => {
          if (isMounted) {
            setImageError(false)
            setDisplayUrl(url || fileUrl)
          }
        })
        .catch(() => {
          if (isMounted) {
            setDisplayUrl(fileUrl)
          }
        })
        .finally(() => {
          if (isMounted) {
            setIsLoading(false)
          }
        })

      return () => {
        isMounted = false
      }
    }

    setDisplayUrl('')
  }, [imageData.file, imageData.fileUrl])

  return (
    <ImageContainer ref={setNodeRef} style={style} $isDragging={isDragging}>
      <ImageOrderBadge>{index + 1}.</ImageOrderBadge>
      <DragHandle type="button" aria-label="Reordenar anexo" {...attributes} {...listeners}>
        <GripVertical size={18} />
      </DragHandle>
      <RemoveImage type="button" onClick={() => onRemove(index)}>
        <X size={18} strokeWidth={3} />
      </RemoveImage>
      {imageError ? (
        <div
          style={{
            width: '100%',
            height: '15rem',
            borderRadius: '1rem',
            backgroundColor: '#F3F4F6',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            color: '#9CA3AF',
            padding: '1rem',
            textAlign: 'center',
          }}
        >
          <ImageIcon size={32} />
          <span style={{ fontSize: '0.813rem', color: '#6B7280' }}>
            Arquivo indisponível no storage
          </span>
        </div>
      ) : isLoading || !displayUrl ? (
        <div
          style={{
            width: '100%',
            height: '15rem',
            borderRadius: '1rem',
            backgroundColor: '#F9FAFB',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            color: '#9CA3AF',
            padding: '1rem',
          }}
        >
          <Loader2 size={28} style={{ animation: 'spin 1s linear infinite' }} />
          <span style={{ fontSize: '0.813rem', color: '#6B7280' }}>
            Carregando imagem...
          </span>
        </div>
      ) : (
        <Image
          draggable={false}
          src={displayUrl}
          alt={imageData.customName}
          onError={() => {
            if (displayUrl) {
              setImageError(true)
            }
          }}
        />
      )}
      <ImageInfo>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <ImageIcon size={18} />
          <span>{getImageSize(imageData)}</span>
        </div>

        <EditInfosAbout style={{ marginBottom: '.5rem' }}>
          <span>Título da imagem</span>
          {imageData.isEditing ? (
            <TitleOrSubtitleInput
              autoFocus
              value={imageData.customName}
              onChange={(e) => onChangeCustomName(index, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onSaveEdit(index, imageData.customName)
                } else if (e.key === 'Escape') {
                  onCancelEdit(index)
                }
              }}
              onBlur={() => onSaveEdit(index, imageData.customName)}
            />
          ) : (
            <TitleOrSubtitle onClick={() => onStartEditing(index)}>
              {imageData.customName}
              <Pen size={14} />
            </TitleOrSubtitle>
          )}
        </EditInfosAbout>

        <EditInfosAbout>
          <span>Subtítulo da imagem</span>
          <TitleOrSubtitleInput
            value={imageData.subtitle}
            onChange={(e) => onChangeSubtitle(index, e.target.value)}
          />
        </EditInfosAbout>
      </ImageInfo>
    </ImageContainer>
  )
}

const ImagesInTheCertificate: React.FC<ImagesInTheCertificateProps> = ({ images, setImages }) => {
  const [isSourceModalOpen, setIsSourceModalOpen] = useState(false)
  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const sortableItems = useMemo(
    () => images.map((imageData, index) => ({
      imageData,
      index,
      sortableId: getSortableId(imageData, index),
    })),
    [images],
  )

  const handleAddImage = (file: File) => {
    setImages((prev) => [
      ...prev,
      {
        file,
        fileSize: file.size,
        customName: file.name,
        isEditing: false,
      },
    ])
  }

  const handleOpenSourceModal = () => {
    setIsSourceModalOpen(true)
  }

  const handleChooseUpload = () => {
    setIsSourceModalOpen(false)
    fileInputRef.current?.click()
  }

  const handleChooseGallery = () => {
    setIsSourceModalOpen(false)
    setIsGalleryModalOpen(true)
  }

  const handleNativeFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      Array.from(e.target.files).forEach((file) => handleAddImage(file))
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleSelectFromGallery = (selectedFiles: GalleryFile[]) => {
    if (!selectedFiles || selectedFiles.length === 0) return
    setIsGalleryModalOpen(false)
    const newItems: ImageData[] = selectedFiles.map((file) => ({
      id: file.id,
      fileUrl: file.signedUrl || file.fileUrl,
      fileSize: file.fileSize,
      customName: file.title || file.originalName || file.fileName || 'Imagem',
      subtitle: '',
      isEditing: false,
    }))
    setImages((prev) => [...prev, ...newItems])
  }

  const handleRemoveImage = (index: number) => {
    setImages(images.filter((_, i) => i !== index))
  }

  const handleStartEditing = (index: number) => {
    setImages(images.map((img, i) => (i === index ? { ...img, isEditing: true } : img)))
  }

  const handleSaveEdit = (index: number, newName: string) => {
    setImages(
      images.map((img, i) =>
        i === index ? { ...img, customName: newName, isEditing: false } : img,
      ),
    )
  }

  const handleCancelEdit = (index: number) => {
    setImages(
      images.map((img, i) =>
        i === index
          ? {
              ...img,
              customName: img.file ? img.file.name : img.customName,
              isEditing: false,
            }
          : img,
      ),
    )
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = sortableItems.findIndex((item) => item.sortableId === active.id)
    const newIndex = sortableItems.findIndex((item) => item.sortableId === over.id)
    if (oldIndex < 0 || newIndex < 0) return

    setImages((prev) => arrayMove(prev, oldIndex, newIndex))
  }

  return (
    <StructureContainer>
      <StructureHeader style={{ marginBottom: '30px' }}>
        <Title>Imagens no Certificado</Title>
      </StructureHeader>
      <InputFiles handleAddImage={handleAddImage} onOpenSourceModal={handleOpenSourceModal} />
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".jpg, .jpeg, .png, image/jpeg, image/png"
        style={{ display: 'none' }}
        onChange={handleNativeFilesSelected}
      />
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToParentElement]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={sortableItems.map((item) => item.sortableId)}
          strategy={rectSortingStrategy}
        >
          <ImagesContainer>
            {sortableItems.map(({ imageData, index, sortableId }) => (
              <SortableImageCard
                key={sortableId}
                imageData={imageData}
                index={index}
                sortableId={sortableId}
                onRemove={handleRemoveImage}
                onStartEditing={handleStartEditing}
                onSaveEdit={handleSaveEdit}
                onCancelEdit={handleCancelEdit}
                onChangeCustomName={(i, value) => {
                  setImages(images.map((img, idx) =>
                    idx === i ? { ...img, customName: value } : img,
                  ))
                }}
                onChangeSubtitle={(i, value) => {
                  setImages(images.map((img, idx) =>
                    idx === i ? { ...img, subtitle: value } : img,
                  ))
                }}
              />
            ))}
          </ImagesContainer>
        </SortableContext>
      </DndContext>

      <AttachmentSourceModal
        isOpen={isSourceModalOpen}
        onClose={() => setIsSourceModalOpen(false)}
        onChooseUpload={handleChooseUpload}
        onChooseGallery={handleChooseGallery}
        title="Imagens no Certificado"
        description="Selecione se deseja enviar novas imagens do seu dispositivo ou escolher fotos existentes na galeria da empresa."
      />

      <AttachmentGalleryModal
        isOpen={isGalleryModalOpen}
        onClose={() => setIsGalleryModalOpen(false)}
        onSelectFiles={handleSelectFromGallery}
        multiple={true}
        defaultFilter="image"
        title="Galeria da Empresa - Imagens no Certificado"
      />
    </StructureContainer>
  )
}

export default ImagesInTheCertificate
